import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AlertCircle,
  ArrowLeft,
  CreditCard,
  Loader2,
  LogIn,
  LucideAngularModule,
  Shield,
  X,
} from 'lucide-angular';
import { FormType, InitConfig } from '@solidgate/angular-sdk';

import { BillingApiService, MerchantData, Product } from '../billing-api.service';
import { AuthService } from '../../../common/auth/auth.service';
import { AppEventsService } from '../../../common/analytics/app-events.service';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';
import { MountedMessage } from '@solidgate/client-sdk-loader';
import { SolidPaymentComponent } from '../solid-payment/solid-payment.component';

type PaymentType = 'card' | 'paypal';

@Component({
  selector: 'app-checkout-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, SolidPaymentComponent],
  templateUrl: './checkout-modal.html',
  styleUrl: './checkout-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutModalComponent {
  private readonly billingService = inject(BillingApiService);
  private readonly authService = inject(AuthService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly merchantData$$ = new BehaviorSubject<MerchantData | null>(null);
  protected readonly merchantData$ = this.merchantData$$.asObservable();

  private readonly paymentType$$ = new BehaviorSubject<PaymentType>('card');
  protected readonly paymentType$ = this.paymentType$$.asObservable();

  private readonly error$$ = new BehaviorSubject<string | null>(null);
  protected readonly error$ = this.error$$.asObservable();

  private readonly isPaymentProcessing$$ = new BehaviorSubject<boolean>(false);
  protected readonly isPaymentProcessing$ = this.isPaymentProcessing$$.asObservable();

  private readonly cardBrandError$$ = new BehaviorSubject<boolean>(false);
  protected readonly cardBrandError$ = this.cardBrandError$$.asObservable();

  private readonly needsAuth$$ = new BehaviorSubject<boolean>(false);
  protected readonly needsAuth$ = this.needsAuth$$.asObservable();

  private selectedProduct: Product | null = null;

  // Icons
  protected readonly icons = {
    X,
    CreditCard,
    Shield,
    Loader2,
    ArrowLeft,
    AlertCircle,
    LogIn,
  };

  // Solidgate form configuration
  protected readonly formParams: InitConfig['formParams'] = {
    formTypeClass: FormType.Flat,
    // cardBrands: [CardBrand.Visa, CardBrand.Mastercard],
    isCardHolderVisible: true,
  };

  protected readonly paypalButtonParams = {
    enabled: true,
    color: 'gold' as const,
    shape: 'pill' as const,
    label: 'buynow' as const,
    height: 48,
  } as Omit<InitConfig['paypalButtonParams'], 'containerId'>;

  // Inputs
  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
  }

  @Input()
  set product(value: Product | null) {
    this.selectedProduct = value;
    if (value) {
      this.initializePayment(value);
    }
  }

  // Outputs
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly success = new EventEmitter<Product>();
  @Output() readonly goBack = new EventEmitter<void>();

  showPayPalLoader$ = new BehaviorSubject<boolean>(true);

  /** Get the selected product */
  protected get currentProduct(): Product | null {
    return this.selectedProduct;
  }

  /** Check if user is authenticated */
  protected get isAuthenticated(): boolean {
    return !!this.authService.getToken();
  }

  /** Initialize payment by fetching merchant data from backend */
  private initializePayment(product: Product): void {
    // Check authentication first
    if (!this.isAuthenticated) {
      this.needsAuth$$.next(true);
      this.isLoading$$.next(false);
      return;
    }

    this.needsAuth$$.next(false);
    this.isLoading$$.next(true);
    this.error$$.next(null);
    this.merchantData$$.next(null);

    this.billingService
      .initializeSubscription(product.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading$$.next(false)),
      )
      .subscribe({
        next: (merchantData) => {
          this.merchantData$$.next(merchantData);
        },
        error: (err) => {
          console.error('Failed to initialize payment:', err);
          // Check if it's an auth error
          if (err.status === 401) {
            this.needsAuth$$.next(true);
          } else {
            this.error$$.next('Failed to initialize payment. Please try again.');
          }
        },
      });
  }

  /** Close the modal */
  protected close(): void {
    this.closed.emit();
    this.reset();
  }

  /** Go back to plan selection */
  protected onGoBack(): void {
    this.goBack.emit();
    this.reset();
  }

  /** Reset modal state */
  private reset(): void {
    this.merchantData$$.next(null);
    this.error$$.next(null);
    this.isPaymentProcessing$$.next(false);
    this.cardBrandError$$.next(false);
    this.needsAuth$$.next(false);
  }

  /** Select payment type */
  protected selectPaymentType(type: PaymentType): void {
    this.paymentType$$.next(type);
  }

  /** Handle payment success */
  protected onPaymentSuccess(event: any): void {
    if (this.selectedProduct) {
      // Extract orderId from Solidgate success event for deduplication with server-side CAPI
      // Solidgate SuccessMessage contains order.order_id
      // const orderId: string | undefined = event?.order?.order_id;
      // Track payment success in Amplitude
      this.amplitudeService.trackEvent('payment_successful', this.getProductEventProps(this.selectedProduct));

      this.success.emit(this.selectedProduct);
    }
    this.close();
  }

  /** Handle payment error */
  protected onPaymentError(event: unknown): void {
    console.error('Payment error:', event);
    this.error$$.next('Payment failed. Please check your card details and try again.');
    this.isPaymentProcessing$$.next(false);

    if (this.selectedProduct) {
      this.amplitudeService.trackEvent('payment_failed', this.getProductEventProps(this.selectedProduct));
    }
  }

  /** Handle payment failure */
  protected onPaymentFail(event: unknown): void {
    console.error('Payment failed:', event);
    this.error$$.next('Payment was declined. Please try a different payment method.');
    this.isPaymentProcessing$$.next(false);

    if (this.selectedProduct) {
      this.amplitudeService.trackEvent('payment_failed', this.getProductEventProps(this.selectedProduct));
    }
  }

  private getProductEventProps(product: Product): Record<string, unknown> {
    return {
      productId: product.id,
      price: product.price.amount,
      interval: product.price.interval,
      hasTrial: product.hasTrial,
    };
  }

  /** Handle card brand validation */
  protected onCardBrandError(hasError: boolean): void {
    this.cardBrandError$$.next(hasError);
  }

  /** Handle SDK mounted event */
  protected onMounted(event: MountedMessage): void {
    if (event.entity === 'paypal') {
      this.showPayPalLoader$.next(false);
    }
  }

  /** Retry payment initialization */
  protected retry(): void {
    if (this.selectedProduct) {
      this.initializePayment(this.selectedProduct);
    }
  }

  /** Get formatted total price for billing summary (shows trial price if applicable) */
  protected getFormattedTotalPrice(product: Product): string {
    if (product.hasTrial && product.price.trialAmount !== undefined) {
      return `$${(product.price.trialAmount / 100).toFixed(2)}`;
    }
    return `$${(product.price.amount / 100).toFixed(2)}`;
  }

  /** Get billing frequency text */
  protected getBillingFrequencyText(product: Product): string {
    if (product.hasTrial && product.price.trialPeriod) {
      const days = Math.round(product.price.trialPeriod / 60 / 24);
      const recurringPrice = `$${(product.price.amount / 100).toFixed(2)}`;
      const interval = product.price.interval === 'year' ? 'year' : 'month';
      return `${days}-day trial, then ${recurringPrice}/${interval}`;
    }
    if (product.price.interval === 'year') {
      return 'billed annually';
    }
    return 'billed monthly';
  }
}
